/**
 * Razorpay Checkout — mobile: phone ki UPI apps upar, "More options" = card/netbanking/wallet.
 */

export function isMobileDevice() {
  if (typeof window === "undefined") return false;
  const ua = navigator.userAgent || "";
  if (/Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(ua)) {
    return true;
  }
  const touch =
    "ontouchstart" in window ||
    (navigator.maxTouchPoints && navigator.maxTouchPoints > 0);
  const narrow = window.matchMedia?.("(max-width: 768px)")?.matches;
  return Boolean(touch && narrow);
}

/**
 * Mobile layout (Razorpay Standard Checkout):
 * 1) UPI — installed apps on device (PhonePe, GPay, Paytm…)
 * 2) More options — Card, Netbanking, Wallet, EMI
 *
 * Note: prefill.method is NOT set — otherwise Razorpay opens only UPI
 * and "More options" is harder to find. User scrolls / taps More options.
 */
function buildMobileCheckoutConfig() {
  return {
    display: {
      blocks: {
        upi_apps: {
          name: "UPI Apps on your phone",
          instruments: [{ method: "upi" }],
        },
        more_options: {
          name: "More options",
          instruments: [
            { method: "card" },
            { method: "netbanking" },
            { method: "wallet" },
            { method: "emi" },
            { method: "paylater" },
          ],
        },
      },
      sequence: ["block.upi_apps", "block.more_options"],
      preferences: {
        show_default_blocks: false,
      },
    },
  };
}

export function loadRazorpayScript() {
  return new Promise((resolve, reject) => {
    if (typeof window !== "undefined" && window.Razorpay) {
      resolve();
      return;
    }
    const existing = document.querySelector('script[src*="checkout.razorpay.com"]');
    if (existing) {
      existing.addEventListener("load", () => resolve());
      existing.addEventListener("error", reject);
      return;
    }
    const script = document.createElement("script");
    script.src = "https://checkout.razorpay.com/v1/checkout.js";
    script.async = true;
    script.onload = () => resolve();
    script.onerror = () => reject(new Error("Failed to load Razorpay checkout"));
    document.body.appendChild(script);
  });
}

/**
 * @param {{ checkout: object, onSuccess: (paymentId: string) => void|Promise<void>, onDismiss?: () => void }} opts
 */
export async function openRazorpayCheckout({ checkout, onSuccess, onDismiss }) {
  if (!checkout?.key || !checkout?.order_id) {
    throw new Error("Invalid checkout session");
  }

  await loadRazorpayScript();

  const contact = String(checkout.prefill?.contact || "")
    .replace(/\D/g, "")
    .slice(-10);
  const email = (checkout.prefill?.email || "").trim();

  const prefill = {
    name: checkout.prefill?.name || "",
    contact,
  };
  if (email) {
    prefill.email = email;
  }

  const isMobile =
    checkout.preferUpiOnMobile !== false && isMobileDevice();

  const options = {
    key: checkout.key,
    amount: checkout.amount,
    currency: checkout.currency || "INR",
    name: checkout.name || "ShowTimeBro",
    description: checkout.description || "Event Tickets",
    order_id: checkout.order_id,
    prefill,
    readonly: checkout.readonly || { contact: true },
    theme: { color: "#f59e0b" },
    handler: async (response) => {
      try {
        await onSuccess(response.razorpay_payment_id);
      } catch (err) {
        throw err;
      }
    },
    modal: {
      ondismiss: () => {
        onDismiss?.();
      },
    },
  };

  if (isMobile) {
    options.config = buildMobileCheckoutConfig();
    options.method = {
      upi: true,
      card: true,
      netbanking: true,
      wallet: true,
      emi: true,
      paylater: true,
    };
  } else if (checkout.config) {
    options.config = checkout.config;
  }

  return new Promise((resolve, reject) => {
    const wrappedHandler = options.handler;
    options.handler = async (response) => {
      try {
        await wrappedHandler(response);
        resolve(response);
      } catch (err) {
        reject(err);
      }
    };

    options.modal.ondismiss = () => {
      onDismiss?.();
      resolve(null);
    };

    const rzp = new window.Razorpay(options);
    rzp.on("payment.failed", (resp) => {
      const msg =
        resp?.error?.description ||
        resp?.error?.reason ||
        "Payment failed. Please try again.";
      reject(new Error(msg));
    });
    rzp.open();
  });
}
